import { useRef, useState } from "react";
import { router } from "@inertiajs/react";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/Textarea";
import { comment_task_path } from "@/lib/routes";
import { fitTextareaHeight } from "@/lib/fitTextareaHeight";

// Tallest the box grows before it scrolls (about five lines).
const MAX_HEIGHT_PX = 120;

// The drawer's comment box, at the foot of the activity feed. Chat-style: one
// line that grows as you type, Comment button inline. Enter adds a line,
// Shift+Enter sends. Posting stores a Comment on the task; onPosted
// lets the drawer refetch the feed so the new comment appears in place.
export default function TaskCommentBox({ taskId, onPosted }: { taskId: string; onPosted: () => void }) {
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);
  const boxRef = useRef<HTMLTextAreaElement>(null);
  const ready = body.trim().length > 0 && !posting;

  const post = () => {
    if (!ready) return;
    setPosting(true);
    router.post(comment_task_path(taskId), { message: body }, {
      preserveScroll: true,
      onSuccess: () => {
        setBody("");
        if (boxRef.current) boxRef.current.style.height = "auto";
        onPosted();
      },
      onFinish: () => setPosting(false),
    });
  };

  return (
    <div className="-mx-5 flex items-end gap-2 border-t border-border px-5 pt-3.5">
      <Textarea
        ref={boxRef}
        rows={1}
        className="min-h-9 flex-1 resize-none overflow-y-hidden py-2 text-[13px] focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-chart-muted"
        value={body}
        onChange={(e) => {
          setBody(e.target.value);
          fitTextareaHeight(e.target, MAX_HEIGHT_PX);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            post();
          }
        }}
        placeholder="Add a comment…"
        aria-label="Comment"
      />
      <Button size="sm" className="h-9 shrink-0" disabled={!ready} onClick={post}>Comment</Button>
    </div>
  );
}
